// src/modules/app-views/toolbox/pages/Ai/index.jsx
// *****************************************************************************
// AI CATALOG PAGE - Tool cataloging with AI
// *****************************************************************************
// IMPORTS
// *****************************************************************************

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button, Card, Flex, Grid, Tooltip } from 'antd';
import { ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { TEMPLATE } from 'constants/ThemeConstant';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { openModal } from 'components/core/GenericModal';
import aiService from '../../service';
import marketplaceService from 'services/marketplaceService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { buildUserMessageContent, buildErrorDetails } from './helpers';
import WelcomeScreen from './components/WelcomeScreen';
import EmptyState from './components/EmptyState';
import ChatMode from './components/ChatMode';
import InfoModal from './modals/InfoModal';
import SettingsModal from './modals/SettingsModal';

const { useBreakpoint } = Grid;

// *****************************************************************************
// CONSTANTS
// *****************************************************************************

// PageHeader with style='card' visible area (title + breadcrumb)
const PAGE_HEADER_HEIGHT = 55;

// *****************************************************************************
// MAIN COMPONENT
// *****************************************************************************

const ToolboxAi = () => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { api } = useDocumentContext();
  const navType = useZustand('theme', 'navType');
  const storedParameters = useZustand('app', 'parameters');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedAppState = useZustand('app', 'state');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const setParametersStore = useZustand('app', 'setParameters', false);
  const setPageBackStore = useZustand('module', 'setPageBack');
  const setPageStore = useZustand('module', 'setPage');
  const setLoadingPageStore = useZustand('boot', 'setLoadingPage');
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen', false);
  const storedSettingsDrawerOpen = useZustand('theme', 'settingsDrawerOpen');

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const formRef = useRef(null);
  const typingTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const skipScrollToBottomRef = useRef(false);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  // Detect mobile (breakpoint md = 768px)
  const isMobile = !screens.md;

  // Read avatar visibility from current page config (toolbox AI has avatar.visible: false)
  const showAvatar = storedCurrentPage?.header?.avatar?.visible !== false;

  // Get the correct content gutter based on screen size
  const contentGutter = isMobile
    ? TEMPLATE.LAYOUT_CONTENT_GUTTER_SM
    : TEMPLATE.LAYOUT_CONTENT_GUTTER;

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInputMode, setShowInputMode] = useState(false);
  const [catalogedToolId, setCatalogedToolId] = useState(null);
  const [selectedModel, setSelectedModel] = useState('deepseek');
  const [lastFormValues, setLastFormValues] = useState(null);
  const [typingProgress, setTypingProgress] = useState({});
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [introTypingComplete, setIntroTypingComplete] = useState(false);

  // Credits state
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // Hide loading page when component is mounted (autoFetch: false page)
  useEffect(() => {
    if (api?.done) {
      api.done(true);
    }
  }, [api?.done]);

  // Fetch credits balance on mount and after cataloging
  const fetchCreditsBalance = useCallback(async () => {
    setIsLoadingCredits(true);
    try {
      const result = await marketplaceService.refreshCreditsBalance();

      if (result?.success) {
        setCreditsBalance(result.balance || 0);
      }
    } catch (error) {
      console.error('[ToolboxAi] Error fetching credits balance:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  }, []);

  // Fetch AI prices if not available in store (fallback for direct navigation)
  const fetchAiPrices = useCallback(async () => {
    try {
      const response = await aiService.getAiPrices();

      // Extract models from various possible response structures
      const models = response?.data?.data?.models
        || response?.data?.models
        || response?.models
        || [];

      if (models.length > 0) {
        // Store in Zustand for future use
        const currentParams = storedParameters || { application: {}, modules: {} };
        const updatedParams = {
          ...currentParams,
          modules: {
            ...currentParams.modules,
            toolbox: {
              ...currentParams.modules?.toolbox,
              aiPrices: models.map(model => ({
                ...model,
                recommended: model.alias === 'deepseek',
              })),
            },
          },
        };
        // Update Zustand store
        if (setParametersStore) {
          setParametersStore(updatedParams);
        }
      }
    } catch (error) {
      console.error('[ToolboxAi] Error fetching AI prices:', error);
    }
  }, [storedParameters, setParametersStore]);

  // Track subscription credits to detect changes
  const lastCreditsRef = useRef(null);
  const wasDrawerOpenRef = useRef(false);

  useEffect(() => {
    fetchCreditsBalance();
  }, [fetchCreditsBalance]);

  // Refresh credits when subscription credits change (e.g., after acquiring credits)
  useEffect(() => {
    const currentCredits = storedSubscription?.credits;
    if (
      currentCredits !== undefined &&
      lastCreditsRef.current !== null &&
      currentCredits !== lastCreditsRef.current
    ) {
      fetchCreditsBalance();
    }
    lastCreditsRef.current = currentCredits;
  }, [storedSubscription?.credits, fetchCreditsBalance]);

  // Refresh credits when settings drawer closes (user may have purchased credits)
  useEffect(() => {
    if (wasDrawerOpenRef.current && !storedSettingsDrawerOpen) {
      fetchCreditsBalance();
    }
    wasDrawerOpenRef.current = storedSettingsDrawerOpen;
  }, [storedSettingsDrawerOpen, fetchCreditsBalance]);

  // Fetch AI prices if not available
  useEffect(() => {
    const currentAiPrices = storedParameters?.modules?.toolbox?.aiPrices;
    if (!currentAiPrices?.length && storedParameters !== null) {
      fetchAiPrices();
    }
  }, [storedParameters, fetchAiPrices]);

  // Calculate container height based on navigation type and screen size
  const containerHeight = useMemo(() => {
    const topNavHeight = navType === 'top' && !isMobile ? TEMPLATE.TOP_NAV_HEIGHT : 0;

    const totalOffset =
      TEMPLATE.HEADER_HEIGHT +
      topNavHeight +
      TEMPLATE.FOOTER_HEIGHT +
      contentGutter * 2 +
      PAGE_HEADER_HEIGHT;

    return `calc(100vh - ${totalOffset + 30}px)`;
  }, [navType, isMobile, contentGutter]);

  // Get AI models from stored parameters
  const aiModels = useMemo(() => {
    return storedParameters?.modules?.toolbox?.aiPrices || [];
  }, [storedParameters]);

  // Calculate required credits for selected model
  const creditsInfo = useMemo(() => {
    const selectedModelData = aiModels.find(m => m.alias === selectedModel);
    // Use ?? (nullish coalescing) to handle 0 correctly; try multiple field names
    const priceBRL = selectedModelData?.pricePerTool
      ?? selectedModelData?.pricePerService
      ?? selectedModelData?.pricePerProduct
      ?? selectedModelData?.price
      ?? 0.50; // Default to deepseek price
    // Convert BRL to credits (1 credit = R$ 0.10)
    const requiredCredits = Math.ceil(priceBRL * 10);
    const hasSufficientCredits = creditsBalance !== null && creditsBalance >= requiredCredits;

    return {
      priceBRL,
      requiredCredits,
      availableCredits: creditsBalance || 0,
      hasSufficientCredits,
      isLoading: isLoadingCredits,
    };
  }, [aiModels, selectedModel, creditsBalance, isLoadingCredits]);

  useEffect(() => {
    // Skip scroll to bottom when a new search is submitted (we want to stay at top)
    if (skipScrollToBottomRef.current) {
      skipScrollToBottomRef.current = false;
      return;
    }
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (showInputMode) {
      setTimeout(() => {
        formRef.current?.goFocus('toolDescription');
      }, 100);
    }
  }, [showInputMode]);

  // Restore form values when transitioning to chat mode
  useEffect(() => {
    if (lastFormValues && messages.length > 0) {
      setTimeout(() => {
        formRef.current?.form?.setFieldsValue(lastFormValues);
      }, 50);
    }
  }, [lastFormValues, messages.length]);

  // Set initial selectedModel when aiModels loads
  useEffect(() => {
    if (aiModels.length > 0 && selectedModel === 'deepseek') {
      const recommendedModel = aiModels.find(m => m.recommended);
      if (recommendedModel) {
        setSelectedModel(recommendedModel.alias);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiModels.length]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleInsertWithAi = useCallback(() => {
    setShowInputMode(true);
  }, []);

  const handleBackToWelcome = useCallback(() => {
    // Clear timers if running
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setShowInputMode(false);
    setMessages([]);
    setCatalogedToolId(null);
    setLastFormValues(null);
    setTypingProgress({});
    setElapsedSeconds(0);
    setIntroTypingComplete(false);
  }, []);

  const handleInsertManually = useCallback(
    (toolId = 'add') => {
      setLoadingPageStore({
        visible: true,
        extendedDelayToHide: 400,
      });

      setPageStore({
        navigate,
        pageName: 'document',
        params: { _id: toolId },
      });
    },
    [navigate, setLoadingPageStore, setPageStore]
  );

  const handleGoBack = useCallback(() => {
    setLoadingPageStore({
      visible: true,
      extendedDelayToHide: 200,
      message: translateX('redirecting'),
    });
    setPageBackStore({ navigate });
  }, [navigate, setPageBackStore, setLoadingPageStore]);

  const handleOpenCreditsDrawer = useCallback(() => {
    setSettingsDrawerOpen(true);
  }, [setSettingsDrawerOpen]);

  const handleOpenHelp = useCallback(() => {
    openModal(InfoModal);
  }, []);

  const handleOpenSettings = useCallback(() => {
    const aiAssistantProfile =
      storedParameters?.modules?.toolbox?.aiSettings?.aiAssistantProfile || '';
    const aiBusinessContext = storedSubscription?.aiBusinessContext || '';
    openModal(SettingsModal, { aiBusinessContext, aiAssistantProfile });
  }, [storedParameters, storedSubscription]);

  // Called when intro typing finishes
  const handleIntroComplete = useCallback(() => {
    setIntroTypingComplete(true);

    // Define the order of sections to reveal (toolbox-specific)
    const sectionsToReveal = [
      'name',
      'classification',
      'specs',
      'pricing',
      'usage',
      'maintenance',
      'references',
      'meta',
      'editLink',
    ];

    let currentIndex = 0;

    // Reveal sections progressively (1500ms between sections for engaging UX)
    typingTimerRef.current = setInterval(() => {
      if (currentIndex < sectionsToReveal.length) {
        const section = sectionsToReveal[currentIndex];
        setTypingProgress(prev => ({
          ...prev,
          [section]: true,
        }));

        // Auto-scroll when sections appear
        if (chatContainerRef?.current) {
          setTimeout(() => {
            chatContainerRef.current.scrollTo({
              top: chatContainerRef.current.scrollHeight,
              behavior: 'smooth',
            });
          }, 50);
        }

        currentIndex++;
      } else {
        if (typingTimerRef.current) {
          clearInterval(typingTimerRef.current);
          typingTimerRef.current = null;
        }
      }
    }, 1500);
  }, []);

  // Typing effect for tool data reveal
  const startTypingEffect = useCallback(() => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    setTypingProgress({});
    setIntroTypingComplete(false);
  }, []);

  const handleFormFinish = useCallback(
    async ({ values }) => {
      const { toolDescription } = values;

      setIsProcessing(true);

      // Silent validation: if empty, just focus the field
      if (!toolDescription?.trim()) {
        formRef.current?.goFocus('toolDescription');
        return;
      }

      const trimmedDescription = toolDescription.trim();

      // Protect against duplicate: check if same description is already being processed
      const isDuplicate = messages.some(
        m => m.role === 'user' && m.data?.toolDescription?.trim() === trimmedDescription
      );
      if (isDuplicate) {
        appService.message({
          flag: 'w',
          text: translateX('tool_already_being_cataloged'),
        });
        formRef.current?.goFocus('toolDescription');
        return;
      }

      // Build user message summary
      const userMessageContent = buildUserMessageContent(values);

      // Add user message
      const userMessage = {
        role: 'user',
        content: userMessageContent,
        data: values,
      };

      // Add AI thinking message (with unique index to track which one to update)
      const aiMessageIndex = messages.length + 1; // index of the AI message in the updated array
      const aiMessage = {
        role: 'assistant',
        content: '',
        status: 'thinking',
        _trackIndex: aiMessageIndex,
      };

      // First submission: scroll to top (transitioning from EmptyState to ChatMode)
      // Subsequent submissions: scroll to bottom (stay in chat flow)
      const isFirstSubmission = messages.length === 0;

      if (isFirstSubmission) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (chatContainerRef?.current) {
          chatContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          if (chatContainerRef?.current) {
            chatContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
        // Skip the scrollToBottom that would be triggered by messages useEffect
        skipScrollToBottomRef.current = true;
      }

      setMessages(prev => [...prev, userMessage, aiMessage]);

      // Clear field and focus immediately for next entry
      setLastFormValues(null);
      formRef.current?.form?.setFieldsValue({ toolDescription: '' });
      setTimeout(() => formRef.current?.goFocus('toolDescription'), 100);

      // Start elapsed time counter
      setElapsedSeconds(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);

      try {
        // Call AI catalog API with selected model
        const response = await aiService.catalogTool({
          toolDescription: trimmedDescription,
          model: selectedModel,
        });

        const toolData = response?.data;

        if (toolData?.success) {
          const catalogedTool = toolData;
          const meta = toolData._aiMeta;
          const creditsDebit = toolData._creditsDebit;
          const alreadyExists = toolData.alreadyExists || meta?.finalCostBRL === 0;

          if (catalogedTool?._id) {
            setCatalogedToolId(catalogedTool._id);
          }

          // Update the specific AI message by its tracked index
          setMessages(prev => {
            const updated = [...prev];
            const idx = updated.findIndex(m => m._trackIndex === aiMessageIndex);
            if (idx !== -1) {
              updated[idx] = {
                role: 'assistant',
                content: alreadyExists
                  ? translateX('tool_already_exists')
                  : translateX('tool_cataloged_successfully'),
                status: 'success',
                tool: catalogedTool,
                meta: alreadyExists
                  ? { ...meta, finalCostBRL: 0, totalTokens: 0, responseTimeMs: 0 }
                  : meta,
                creditsDebit: alreadyExists ? null : creditsDebit,
                alreadyExists,
              };
            }
            return updated;
          });

          // Refresh parameters if new tool (await to ensure new categories are loaded)
          if (!alreadyExists) {
            if (typeof storedAppState?.refreshParameters === 'function') {
              await storedAppState.refreshParameters();
            }
            // Refresh credits balance after successful catalog
            fetchCreditsBalance();

            // Show credits debit notification
            if (creditsDebit?.debited > 0) {
              appService.notification({
                flag: 'info',
                title: 'credits_debited',
                description: translateX('credits_debited_description', {
                  debited: creditsDebit.debited,
                  operation: translateX('ai_tool_cataloging'),
                  balance: creditsDebit.balanceAfter,
                }),
              });
            }
          }

          // Start typing effect for the tool data
          startTypingEffect(catalogedTool);
        } else {
          // Update the specific AI message with error
          const errorDetails = buildErrorDetails({
            message: toolData?.message || response?.message,
            code: toolData?.code || response?.code,
            details: toolData?.details || response?.details,
            statusCode: response?.status,
          });
          setMessages(prev => {
            const updated = [...prev];
            const idx = updated.findIndex(m => m._trackIndex === aiMessageIndex);
            if (idx !== -1) {
              updated[idx] = {
                role: 'assistant',
                status: 'error',
                errorDetails,
              };
            }
            return updated;
          });
        }
      } catch (error) {
        console.error('AI Catalog error:', error);
        const errorDetails = buildErrorDetails({
          message: error?.response?.data?.message || error?.message,
          code: error?.response?.data?.code || error?.code,
          details: error?.response?.data?.details,
          statusCode: error?.response?.status,
          isNetworkError: !error?.response,
        });
        setMessages(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(m => m._trackIndex === aiMessageIndex);
          if (idx !== -1) {
            updated[idx] = {
              role: 'assistant',
              status: 'error',
              errorDetails,
            };
          }
          return updated;
        });
      } finally {
        // Stop elapsed time counter
        if (elapsedTimerRef.current) {
          clearInterval(elapsedTimerRef.current);
          elapsedTimerRef.current = null;
        }
        setIsProcessing(false);
      }
    },
    [selectedModel, messages, startTypingEffect, fetchCreditsBalance, storedAppState]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------

  // Determine what to render
  const renderContent = () => {
    if (!showInputMode) {
      return (
        <WelcomeScreen
          isMobile={isMobile}
          onInsertWithAi={handleInsertWithAi}
          onInsertManually={handleInsertManually}
        />
      );
    }

    if (messages.length === 0) {
      return (
        <EmptyState
          formRef={formRef}
          lastFormValues={lastFormValues}
          selectedModel={selectedModel}
          aiModels={aiModels}
          creditsInfo={creditsInfo}
          isMobile={isMobile}
          onModelChange={setSelectedModel}
          onFinish={handleFormFinish}
          onBackToWelcome={handleBackToWelcome}
          onOpenCreditsDrawer={handleOpenCreditsDrawer}
        />
      );
    }

    return (
      <ChatMode
        messages={messages}
        formRef={formRef}
        lastFormValues={lastFormValues}
        selectedModel={selectedModel}
        aiModels={aiModels}
        creditsInfo={creditsInfo}
        isProcessing={isProcessing}
        elapsedSeconds={elapsedSeconds}
        typingProgress={typingProgress}
        introTypingComplete={introTypingComplete}
        catalogedToolId={catalogedToolId}
        chatContainerRef={chatContainerRef}
        messagesEndRef={messagesEndRef}
        isMobile={isMobile}
        showAvatar={showAvatar}
        onModelChange={setSelectedModel}
        onFinish={handleFormFinish}
        onBackToWelcome={handleBackToWelcome}
        onIntroComplete={handleIntroComplete}
        onEditTool={handleInsertManually}
        onOpenCreditsDrawer={handleOpenCreditsDrawer}
      />
    );
  };

  return (
    <div className="container mt-3">
      <Card
        styles={{
          body: {
            padding: isMobile ? '26px 16px 16px' : '26px 26px 0',
            display: 'flex',
            flexDirection: 'column',
            ...(isMobile ? {} : { height: '100%' }),
          },
        }}
        style={{
          // Desktop: fixed height with scroll, Mobile: auto height
          ...(isMobile ? {} : { height: containerHeight, overflow: 'hidden' }),
          marginBottom: isMobile ? 0 : -contentGutter - 10,
        }}
      >
        {/* Action buttons bar */}
        <Flex justify="flex-end" gap={8} style={{ marginBottom: 8 }}>
          <Tooltip title={translateX('ai_settings')}>
            <Button icon={<SettingOutlined />} onClick={handleOpenSettings} />
          </Tooltip>
          <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
            {translateX('exit')}
          </Button>
        </Flex>

        {renderContent()}
      </Card>
    </div>
  );
};

// *****************************************************************************
// EXPORT
// *****************************************************************************

export default ToolboxAi;
